// Changing an event as the board shows it, whichever calendar it is in (Calendar plan, step 3): the board's own events
// change as entries, with the board's Undo; a Google calendar's are written to Google first, through the board, and
// what Google answers is what shows. Every change goes on the same Undo, and an undone change goes back to Google
// too. One time of a repeating event asks first: it alone, it and the ones after it, or every one.
//
// Times in a Change are on the event's own clock (as kept); moves made on screen are on the viewer's, so they come
// through moveShown and sizeShown, which put them on the event's clock first.

import { create } from "zustand";
import { BOARD_CAL, addLocal, calSetOf, cleanEvent, eventOf, localAt, localMinutes, momentOf, type EventData, type Shown } from "../../../shared/agenda";
import { repeatDays, type RepeatRule } from "../../../shared/repeat";
import { addDays, daysBetween } from "../../../shared/days";
import { newId } from "../../../shared/ids";
import { orderAt } from "../../../shared/entries";
import type { Entry, Op } from "../../../shared/types";
import { commit, deleteEntry, putEntries, recordOutside, undo } from "../../state/actions";
import { boardEntries, get, toast } from "../../state/store";
import { gcalWrite, pendingShow, useGcal, useGcalPending, type WriteEvent } from "../../state/gcal";
import { zoneHere } from "./actions";
import { addCopy, deleteWithCopies, guestsOf, notifyOnce, writableKeys } from "./copies";
import { isLayer, layerMoved } from "../../../shared/layers";

export type Scope = "one" | "following" | "all";

/** A change to an event, its times on the event's own clock. null clears a place, notes, a color or a repeat. */
export type Change = WriteEvent;

/** Whether the board may change an event: its own, or one in a Google calendar this account writes to and that works. */
export function canChange(s: Pick<Shown, "cal">): boolean {
  if (s.cal === BOARD_CAL) return true;
  // a layer item (step 6): a dated thing on the user's own board, which moves there
  if (isLayer(s.cal)) return true;
  const st = useGcal.getState().status;
  const acct = st?.accounts.find((a) => a.calendars.some((c) => c.key === s.cal));
  const cal = acct?.calendars.find((c) => c.key === s.cal);
  return !!cal && !acct?.error && (cal.role === "owner" || cal.role === "writer");
}

/**
 * Whether the board may move an event or change its words: one it may change at all, and not someone else's
 * invitation, whose time and words are its organizer's (you answer it and color it: step 5).
 */
export function canEdit(s: Pick<Shown, "cal" | "meta">): boolean {
  return canChange(s) && !s.meta?.from;
}

/** A Google event's own id, from its id on the board ("<calendar>#<id>"). */
export const googleId = (s: Pick<Shown, "id" | "cal">) => s.id.slice(s.cal.length + 1);

/** The event as Google last gave it (for its version), from what the board read. */
function googleEvent(cal: string, id: string) {
  for (const e of Object.values(useGcal.getState().events)) {
    const hit = e?.list.find((x) => x.cal === cal && x.id === id);
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------
// which of a repeating event

interface ScopeAsk {
  verb: "change" | "delete" | "reply";
  at: { x: number; y: number };
  answer: (s: Scope | null) => void;
  /** this and the following, for a rule the board cannot split (a Google repeat made with more parts) */
  following: boolean;
}

export const useScopeAsk = create<{ ask: ScopeAsk | null }>(() => ({ ask: null }));

/** Asks which times of a repeating event a change is for; null when the person backs out. */
export function askScope(verb: ScopeAsk["verb"], at: { x: number; y: number }, following = true): Promise<Scope | null> {
  return new Promise((resolve) => {
    useScopeAsk.setState({
      ask: {
        verb,
        at,
        following,
        answer: (s) => {
          useScopeAsk.setState({ ask: null });
          resolve(s);
        },
      },
    });
  });
}

/** Where the pointer last was, for the question to open by it (the window's middle before any press). */
let pointer: { x: number; y: number } | null = null;
if (typeof window !== "undefined" && typeof window.addEventListener === "function") window.addEventListener("pointerup", (e) => (pointer = { x: e.clientX, y: e.clientY }), true);

/** The times a change is for: every time of a single event; for one time of a repeat, as the person says. */
async function scopeFor(s: Shown, verb: ScopeAsk["verb"]): Promise<Scope | null> {
  if (!s.series) return "one";
  // an answer is for this time or all of them, as Google Calendar asks it
  return askScope(verb, pointer ?? { x: innerWidth / 2, y: innerHeight / 2 }, verb !== "reply");
}

/** Whether Google emails an event's guests about a change: asked first, as Google Calendar asks; null backs out. */
interface NotifyAsk {
  at: { x: number; y: number };
  answer: (send: boolean | null) => void;
}
export const useNotifyAsk = create<{ ask: NotifyAsk | null }>(() => ({ ask: null }));
function askNotify(): Promise<boolean | null> {
  return new Promise((resolve) =>
    useNotifyAsk.setState({
      ask: {
        at: pointer ?? { x: innerWidth / 2, y: innerHeight / 2 },
        answer: (send) => {
          useNotifyAsk.setState({ ask: null });
          resolve(send);
        },
      },
    }),
  );
}

// ---------------------------------------------------------------------------
// layers (step 6): a post, a follow-up or a task, moved on its own board

/**
 * A layer item moved on the calendar: its day (and time) changed on its own board, as if moved there, on the board's
 * Undo. Times are the user's own, so the viewer's clock is theirs. A post keeps its time when moved a day.
 */
function moveLayer(s: Shown, change: Change) {
  const layer = s.meta!.layer!;
  const e = get().entries[layer.entry];
  const data = e && layerMoved(layer.kind, e.data, change);
  if (!e || !data) return;
  // a post moved to another day goes last on it, as the content calendar puts a post moved there
  const order =
    layer.kind === "post" && data.day !== e.data.day
      ? (() => {
          const same = Object.values(get().entries)
            .filter((x) => x.boardId === e.boardId && x.t === "post" && x.id !== e.id && x.data.day === data.day)
            .sort((a, b) => a.order - b.order);
          return orderAt(same, same.length);
        })()
      : e.order;
  putEntries([{ ...e, order, data }], layer.kind === "post" ? "Move post" : "Move follow-up");
}

// ---------------------------------------------------------------------------
// the board's own events

/** A board event's occurrence as an event of its own: its times, no repeat. */
function occurrence(s: Shown): EventData {
  // its copies in Google are the series', so one time of it starts without any (made again after, with the same calendars)
  const { repeat: _r, g: _g, ...rest } = s.ev;
  return { ...rest };
}

/** A time of a repeating event made an event of its own: its copies in the series' Google calendars are made too. */
function copyAsSeries(from: EventData, entry: Entry) {
  for (const key of Object.keys(from.g ?? {})) void addCopy(entry.id, key);
}

/** How many times a rule happened before a day, from its first. */
const usedBefore = (rule: RepeatRule, first: string, day: string) => repeatDays({ ...rule, skip: [] }, first, first, addDays(day, -1)).length;

function changeBoard(s: Shown, change: Change, label: string, scope: Scope) {
  const series = s.series ? get().entries[s.series] : get().entries[s.id];
  if (!series || series.t !== "event") return;
  const ev = eventOf(series);
  const merged = (base: EventData, with_: Change = change): EventData => {
    try {
      return cleanEvent({ ...base, ...with_ }, null);
    } catch (e) {
      toast({ level: "warn", title: "That change was not made", body: e instanceof Error ? e.message : null });
      throw e;
    }
  };
  try {
    if (!s.series || !s.occ || !ev.repeat) {
      putEntries([{ ...series, data: { ...merged(ev) } }], label);
      return;
    }
    const first = ev.start.slice(0, 10);
    if (scope === "all" || (scope === "following" && s.occ <= first)) {
      // the whole series moves by as much as this time moved, on the event's own clock
      const next: Change = { ...change };
      if (change.start !== undefined && change.end !== undefined) {
        if (ev.allDay && change.allDay !== false) {
          const shift = daysBetween(s.ev.start, change.start);
          next.start = addDays(ev.start, shift);
          next.end = addDays(next.start, daysBetween(change.start, change.end));
        } else if (!ev.allDay && !change.allDay) {
          const shift = localMinutes(s.ev.start, change.start);
          next.start = addLocal(ev.start, shift);
          next.end = addLocal(next.start, localMinutes(change.start, change.end));
        }
      }
      putEntries([{ ...series, data: { ...merged(ev, next) } }], label);
      return;
    }
    const rule = ev.repeat;
    const skipped = [...new Set([...(rule.skip ?? []), s.occ])];
    if (scope === "one") {
      const now = Date.now();
      const single: Entry = { id: newId("e_"), boardId: series.boardId, t: "event", parent: null, order: 0, data: { ...merged(occurrence(s)) }, createdAt: now, updatedAt: now, by: null };
      commit([{ op: "entry.put", entry: { ...series, data: { ...ev, repeat: { ...rule, skip: skipped } } } }, { op: "entry.put", entry: single }], label);
      copyAsSeries(ev, single);
      return;
    }
    // this and the following: the series ends the day before, and a new one starts here with the change
    const used = usedBefore(rule, first, s.occ);
    const ends: RepeatRule = { ...rule, until: addDays(s.occ, -1) };
    delete ends.count;
    const goesOn: RepeatRule = { ...rule, ...(rule.count ? { count: Math.max(1, rule.count - used) } : {}), skip: (rule.skip ?? []).filter((d) => d >= s.occ!) };
    const now = Date.now();
    const next: Entry = { id: newId("e_"), boardId: series.boardId, t: "event", parent: null, order: 0, data: { ...merged({ ...occurrence(s), repeat: goesOn }) }, createdAt: now, updatedAt: now, by: null };
    commit([{ op: "entry.put", entry: { ...series, data: { ...ev, repeat: ends } } }, { op: "entry.put", entry: next }], label);
    copyAsSeries(ev, next);
  } catch {
    /* said above */
  }
}

function deleteBoard(s: Shown, scope: Scope, notify = false) {
  const series = s.series ? get().entries[s.series] : null;
  if (!series || !s.occ) return void (eventOf(get().entries[s.id] ?? ({ data: {} } as Entry)).g ? deleteWithCopies(s.id, notify) : deleteEntry(s.id, "event"));
  const ev = eventOf(series);
  const rule = ev.repeat;
  if (!rule || scope === "all" || (scope === "following" && s.occ <= ev.start.slice(0, 10))) return void (ev.g ? deleteWithCopies(series.id, notify) : deleteEntry(series.id, "event"));
  const next: RepeatRule = scope === "one" ? { ...rule, skip: [...new Set([...(rule.skip ?? []), s.occ])] } : (() => {
    const r: RepeatRule = { ...rule, until: addDays(s.occ!, -1) };
    delete r.count;
    return r;
  })();
  commit([{ op: "entry.put", entry: { ...series, data: { ...ev, repeat: next } } } satisfies Op], scope === "one" ? "Delete one time" : "Delete this and later times");
  toast({ level: "info", title: scope === "one" ? "Took that time out" : "Ended it the day before", action: { label: "Undo", run: undo } });
}

// ---------------------------------------------------------------------------
// Google's events

/**
 * Writes to one event (to one repeat, for its times) go in turn: each waits for the one before it, so it carries the
 * version Google gave that one, and two quick changes never clash with each other.
 */
const turns = new Map<string, Promise<unknown>>();
function inTurn<T>(s: Shown, job: () => Promise<T>): Promise<T> {
  const key = `${s.cal}#${s.series ?? googleId(s)}`;
  const next = (turns.get(key) ?? Promise.resolve()).catch(() => undefined).then(job);
  turns.set(key, next);
  void next.finally(() => turns.get(key) === next && turns.delete(key)).catch(() => undefined);
  return next;
}

/** A change shown before Google answers, added to any still waiting on the same event; cleared once none waits. */
const waiting = new Map<string, number>();
function showWaiting(s: Shown, change: Change & { hidden?: boolean }) {
  waiting.set(s.id, (waiting.get(s.id) ?? 0) + 1);
  pendingShow(s.id, { ...(useGcalPending.getState().shown[s.id] ?? {}), ...change });
}
function doneWaiting(s: Shown) {
  const n = (waiting.get(s.id) ?? 1) - 1;
  if (n > 0) return void waiting.set(s.id, n);
  waiting.delete(s.id);
  pendingShow(s.id, null);
}

/** Old values of the fields a change sets, for its Undo. */
function inverseOf(s: Shown, change: Change): Change {
  const g = googleEvent(s.cal, googleId(s));
  const back: Change = {};
  if (change.title !== undefined) back.title = s.ev.title;
  if (change.location !== undefined) back.location = s.ev.location ?? null;
  if (change.notes !== undefined) back.notes = s.ev.notes ?? null;
  if (change.color !== undefined) back.color = colorName(g?.color) ?? null;
  if (change.start !== undefined) Object.assign(back, { allDay: s.ev.allDay, start: s.ev.start, end: s.ev.end, tz: s.ev.tz });
  if (change.repeat !== undefined) back.repeat = null;
  return back;
}

/** A Google event color's name on the board, from its hex. */
export function colorName(hex: string | undefined): string | null {
  if (!hex) return null;
  const table: Record<string, string> = { "#d50000": "red", "#e67c73": "red", "#f4511e": "orange", "#f6bf26": "yellow", "#33b679": "green", "#0b8043": "green", "#039be5": "blue", "#3f51b5": "blue", "#7986cb": "purple", "#8e24aa": "purple", "#616161": "grey" };
  return table[hex.toLowerCase()] ?? null;
}

async function changeGoogle(s: Shown, change: Change, label: string, scope: Scope, retry = false, notify = false): Promise<void> {
  const id = googleId(s);
  const back = inverseOf(s, change);
  if (scope === "one") showWaiting(s, change);
  try {
    await inTurn(s, async () => {
      const g = googleEvent(s.cal, id);
      const r = await gcalWrite({ op: "update", cal: s.cal, id, etag: g?.etag, notify, event: { ...change, ...(change.start !== undefined && !change.allDay ? { tz: change.tz ?? s.ev.tz } : {}) }, ...(s.series ? { scope, series: s.series, occ: s.occ } : {}), before: { allDay: s.ev.allDay, start: s.ev.start, end: s.ev.end, tz: s.ev.tz } });
      if (r.guestsChanged) {
        // who is asked changed in Google, or the card never read them: nothing sent, and never sent again on that list
        toast({ level: "warn", title: "Who is asked changed in Google meanwhile", body: "Nothing was sent. Open the event to see its guests now, then change them again.", ms: 12_000 });
        return;
      }
      if (r.clash) {
        // changed in Google meanwhile: the newer one shows, and the person's change waits on their word
        toast({
          level: "warn",
          title: "It changed in Google meanwhile",
          body: "The board shows Google's newer version. Your change was not made.",
          ms: 12_000,
          action: retry ? undefined : { label: "Apply my change again", run: () => void reapply(s, change, label, scope, notify) },
        });
        return;
      }
      if (scope === "following") {
        toast({ level: "info", title: "Changed this and the later times in Google", body: "This one can't be undone from the board: change it back in Google if you need to." });
        return;
      }
      // guests invited or a call made cannot be taken back from the board: Google has told the guests, or made the link
      if (change.guests !== undefined || change.meet) {
        if (notify) toast({ level: "info", title: "Google emailed your guests" });
        return;
      }
      recordOutside(label, {
        undo: () => void redoOn(s, back, scope, notify),
        redo: () => void redoOn(s, change, scope, notify),
      });
    });
  } catch (e) {
    toast({ level: "warn", title: "Google did not take that change", body: e instanceof Error ? e.message : null });
  } finally {
    if (scope === "one") doneWaiting(s);
  }
}

/** A change sent again once the person has seen Google's newer version, on its version now. */
async function reapply(s: Shown, change: Change, label: string, scope: Scope, notify = false) {
  await changeGoogle(s, change, label, scope, true, notify);
}

/** An Undo's or a Redo's change, on the event as the board has it now (its version moved on with each change). */
async function redoOn(s: Shown, change: Change, scope: Scope, notify = false) {
  const id = googleId(s);
  try {
    await inTurn(s, async () => {
      const g = googleEvent(s.cal, id);
      const now: Shown = g ? { ...s, ev: { ...s.ev, start: g.start, end: g.end, allDay: g.allDay, tz: g.tz } } : s;
      const r = await gcalWrite({ op: "update", cal: s.cal, id, etag: g?.etag, notify, event: change, ...(s.series ? { scope, series: s.series, occ: s.occ } : {}), before: { allDay: now.ev.allDay, start: now.ev.start, end: now.ev.end, tz: now.ev.tz } });
      if (r.clash) toast({ level: "warn", title: "It changed in Google meanwhile", body: "Undo was not made, so nothing of Google's newer version is lost." });
    });
  } catch (e) {
    toast({ level: "warn", title: "Google did not take that", body: e instanceof Error ? e.message : null });
  }
}

async function deleteGoogle(s: Shown, scope: Scope, notify = false) {
  const id = googleId(s);
  // the delete under way, which Undo takes back; Redo sends it again, on the same Undo step
  let pending: string | null = null;
  const send = async (): Promise<boolean> => {
    showWaiting(s, { hidden: true });
    try {
      return await inTurn(s, async () => {
        const g = googleEvent(s.cal, id);
        const r = await gcalWrite({ op: "delete", cal: s.cal, id, etag: g?.etag, ...(notify ? { notify: true } : {}), ...(s.series ? { scope, series: s.series, occ: s.occ } : {}) });
        pending = r.pending ?? null;
        return true;
      });
    } catch (e) {
      toast({ level: "warn", title: "Google did not delete it", body: e instanceof Error ? e.message : null });
      return false;
    } finally {
      doneWaiting(s);
    }
  };
  const back = async () => {
    if (!pending) return;
    const key = pending;
    pending = null;
    const u = await gcalWrite({ op: "undelete", cal: s.cal, pending: key });
    // too late: Google deleted it, so it is made again as it was (one time of a repeat comes back on its own)
    // a meeting with guests or a call is never made again without them: Google keeps the deleted one in its bin
    if (u.gone && scope === "one" && !s.meta?.guests && !s.meta?.join) await gcalWrite({ op: "create", cal: s.cal, event: { title: s.ev.title, allDay: s.ev.allDay, start: s.ev.start, end: s.ev.end, tz: s.ev.tz, location: s.ev.location ?? null, notes: s.ev.notes ?? null } });
    else if (u.gone) toast({ level: "warn", title: "It was deleted in Google already", body: "If you need it back, look in Google Calendar's Bin." });
  };
  if (!(await send())) return;
  const takeBack = recordOutside("Delete event", { undo: () => void back(), redo: () => void send() });
  toast({ level: "info", title: scope === "one" ? "Deleted" : scope === "all" ? "Deleted every time it repeats" : "Deleted this and the later times", body: "It leaves Google in 10 seconds.", ms: 10_000, action: { label: "Undo", run: takeBack } });
}

// ---------------------------------------------------------------------------
// what the views and the card call

/** A change to an event as shown: asks which times first for a repeat, then makes it in its calendar. */
export async function changeShown(s: Shown, change: Change, label: string, known?: Scope): Promise<void> {
  if (!canChange(s)) return;
  if (s.meta?.layer) return moveLayer(s, change);
  // someone else's invitation: its color is yours, the rest its organizer's
  const words = Object.keys(change).some((k) => k !== "color");
  if (words && !canEdit(s)) return;
  const scope = known ?? (await scopeFor(s, "change"));
  if (!scope) return;
  if (s.cal === BOARD_CAL) {
    // an event whose copy is a meeting: its guests hear of a change only when the person says so
    const id = s.series ?? s.id;
    const e = get().entries[id];
    if (words && e && e.t === "event" && guestsOf(eventOf(e))) {
      const send = await askNotify();
      if (send === null) return;
      notifyOnce(id, send);
    }
    return changeBoard(s, change, label, scope);
  }
  // its guests hear of a change only when the person says so (a new guest list says it in its own card)
  const notify = change.guests !== undefined ? true : words && s.meta?.guests ? await askNotify() : false;
  if (notify === null) return;
  await changeGoogle(s, change, label, scope, false, notify);
}

/** Your answer to an invitation: this time or all of them for a repeating one; Google tells its organizer. */
export async function replyShown(s: Shown, reply: "accepted" | "tentative" | "declined"): Promise<void> {
  if (!canChange(s) || !s.meta?.you) return;
  const scope = await scopeFor(s, "reply");
  if (!scope) return;
  try {
    await inTurn(s, () => gcalWrite({ op: "reply", cal: s.cal, id: googleId(s), reply, ...(s.series ? { scope, series: s.series, occ: s.occ } : {}) }));
  } catch (e) {
    toast({ level: "warn", title: "Google did not take that answer", body: e instanceof Error ? e.message : null });
  }
}

/** An event moved on screen to start at a time on the viewer's clock, lasting as long as it did. */
export function moveShown(s: Shown, startHere: string, label = "Move event") {
  if (s.ev.allDay) return;
  const t = momentOf(startHere, zoneHere());
  const minutes = Math.round((momentOf(s.ev.end, s.ev.tz) - momentOf(s.ev.start, s.ev.tz)) / 60_000);
  void changeShown(s, { allDay: false, start: localAt(t, s.ev.tz), end: localAt(t + minutes * 60_000, s.ev.tz), tz: s.ev.tz }, label);
}

/** An event's end moved on screen to a time on the viewer's clock; at least 15 minutes after it starts. */
export function sizeShown(s: Shown, endHere: string) {
  if (s.ev.allDay) return;
  const start = momentOf(s.ev.start, s.ev.tz);
  const end = Math.max(start + 15 * 60_000, momentOf(endHere, zoneHere()));
  void changeShown(s, { allDay: false, start: s.ev.start, end: localAt(end, s.ev.tz), tz: s.ev.tz }, "Change event length");
}

/** An event moved a number of days on, at the same time of day. */
export function shiftShown(s: Shown, days: number) {
  if (!days) return;
  if (s.ev.allDay) return void changeShown(s, { allDay: true, start: addDays(s.ev.start, days), end: addDays(s.ev.end, days) }, "Move event");
  const startHere = localAt(momentOf(s.ev.start, s.ev.tz), zoneHere());
  moveShown(s, `${addDays(startHere.slice(0, 10), days)}${startHere.slice(10)}`);
}

/** Deletes an event as shown: asks which times first for a repeat; a Google one leaves Google after ten seconds. */
export async function deleteShown(s: Shown): Promise<void> {
  // a layer item is deleted on its own board, never from the calendar
  if (!canChange(s) || s.meta?.layer) return;
  const scope = await scopeFor(s, "delete");
  if (!scope) return;
  if (s.cal === BOARD_CAL) {
    // an event whose copy is a meeting: whether Google tells its guests it is cancelled, asked first
    const e = get().entries[s.series ?? s.id];
    const send = e && e.t === "event" && guestsOf(eventOf(e)) ? await askNotify() : false;
    if (send === null) return;
    return deleteBoard(s, scope, send);
  }
  // a meeting of yours with guests: whether Google tells them it is cancelled, asked first
  const notify = s.meta?.guests && canEdit(s) ? await askNotify() : false;
  if (notify === null) return;
  await deleteGoogle(s, scope, notify);
}

/** The Google calendars this account writes to, on accounts that work: where a new event may go besides the board. */
export function writableCalendars() {
  return useGcal.getState().status?.accounts.filter((a) => !a.error).flatMap((a) => a.calendars.filter((c) => c.role === "owner" || c.role === "writer")) ?? [];
}

/** The Google calendars a new event on a board goes into besides the board: the ones ticked while they still work, never a
 *  Google calendar nobody ticked, where a test could reach real clients (David, 29 Oct). */
export function defaultCalendars(boardId: string): string[] {
  const set = calSetOf(boardEntries(get(), boardId), boardId);
  const ok = writableKeys();
  return (set.into ?? []).filter((k) => ok.has(k));
}
